import { formatEuro } from "@/lib/api";
import { Banknote, X, Check } from "lucide-react";

/**
 * Big readable confirmation dialog before saving a cash transaction.
 * Shows a clear summary: # of cards, total, given, change.
 */
export default function CashConfirmDialog({ open, summary, onConfirm, onCancel, submitting }) {
  if (!open || !summary) return null;
  const { ticketCount, total, given, change, byCategory, voucher, voucherDiscount, customerName } = summary;

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-8 pt-8 pb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <Banknote className="h-6 w-6 text-emerald-700" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">Bestätigen</p>
              <h2 className="text-2xl font-black tracking-tight">Bar kassieren?</h2>
            </div>
          </div>
          {customerName && (
            <p className="text-lg text-zinc-700 mt-2">{customerName}</p>
          )}
        </div>

        <div className="px-8 py-2">
          <ul className="space-y-2 mb-4">
            {byCategory.map((c) => (
              <li key={c.key} className="flex items-center justify-between text-lg border-b border-zinc-100 py-2">
                <span className="font-semibold">{c.qty}× {c.label}</span>
                <span className="font-mono">{formatEuro(c.lineTotal)}</span>
              </li>
            ))}
            {voucher > 0 && (
              <li className="flex items-center justify-between text-lg border-b border-purple-100 py-2 text-purple-800">
                <span className="font-semibold">{voucher}× Gutschein eingelöst</span>
                <span className="font-mono">– {formatEuro(voucherDiscount)}</span>
              </li>
            )}
          </ul>
        </div>

        <div className="mx-8 mb-4 bg-zinc-50 border border-zinc-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-base text-zinc-500">Gesamtpreis ({ticketCount} Karten)</span>
            <span className="font-mono text-2xl font-black tabular-nums">{formatEuro(total)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-base text-zinc-500">Gegeben</span>
            <span className="font-mono text-2xl font-bold tabular-nums">{formatEuro(given)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-zinc-300 pt-3">
            <span className="text-base font-bold text-emerald-800 uppercase tracking-wide">Rückgeld</span>
            <span data-testid="cash-confirm-change" className="font-mono text-4xl font-black text-emerald-700 tabular-nums">{formatEuro(change)}</span>
          </div>
        </div>

        <div className="px-8 py-6 border-t border-zinc-100 flex gap-3">
          <button
            data-testid="cash-confirm-cancel"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 h-16 rounded-xl border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-lg flex items-center justify-center gap-2">
            <X className="h-5 w-5" /> Abbrechen
          </button>
          <button
            data-testid="cash-confirm-ok"
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 h-16 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black text-lg flex items-center justify-center gap-2">
            <Check className="h-6 w-6" /> Ja, kassieren
          </button>
        </div>
      </div>
    </div>
  );
}
